// Enhanced session service that uses both localStorage and Supabase
import { 
  createSupabaseSession, 
  getSupabaseSession, 
  updateSupabaseSession, 
  deleteSupabaseSession, 
  updateSupabaseSessionActivity 
} from './supabaseService';

const STORAGE_KEY = 'crossword_sessions';
const CLEANUP_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours

// Initialize cleanup on load
initializeCleanup();

function initializeCleanup() {
  // Clean up expired sessions on app load
  cleanupExpiredSessions();
  
  // Set up periodic cleanup
  setInterval(cleanupExpiredSessions, CLEANUP_INTERVAL);
}

function cleanupExpiredSessions() {
  try {
    const sessions = getAllLocalSessions();
    const now = new Date();
    const expirationTime = 365 * 24 * 60 * 60 * 1000; // 365 days in milliseconds
    
    const activeSessions = {};
    
    Object.keys(sessions).forEach(sessionId => {
      const session = sessions[sessionId];
      const lastActivity = new Date(session.lastActivity);
      const timeSinceActivity = now - lastActivity;
      
      if (timeSinceActivity < expirationTime) {
        activeSessions[sessionId] = session;
      }
    });
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(activeSessions));
  } catch (error) {
    console.error('Error during cleanup:', error);
  }
}

function getAllLocalSessions() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading sessions:', error);
    return {};
  }
}

function saveLocalSession(sessionId, sessionData) {
  try {
    const sessions = getAllLocalSessions();
    sessions[sessionId] = sessionData;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  } catch (error) {
    console.error('Error saving session:', error);
    throw new Error('Failed to save session');
  }
}

export async function createSession(sessionData) {
  // Try Supabase first, fallback to localStorage
  try {
    const supabaseResult = await createSupabaseSession(sessionData);
    console.log('Session created in Supabase:', supabaseResult);
  } catch (supabaseError) {
    console.warn('Supabase unavailable, using localStorage:', supabaseError.message);
  }
  
  // Always save locally as backup
  saveLocalSession(sessionData.id, sessionData);
  return sessionData;
}

export async function getSession(sessionId) {
  // Try Supabase first, fallback to localStorage
  try {
    const supabaseSession = await getSupabaseSession(sessionId);
    if (supabaseSession) {
      // Update local storage with Supabase data
      saveLocalSession(sessionId, supabaseSession);
      return supabaseSession;
    }
  } catch (supabaseError) {
    console.warn('Supabase unavailable, using localStorage:', supabaseError.message);
  }
  
  // Fallback to localStorage
  const sessions = getAllLocalSessions();
  const session = sessions[sessionId];
  
  if (session) {
    // Update last activity
    session.lastActivity = new Date().toISOString();
    saveLocalSession(sessionId, session);
  }
  
  return session || null;
}

export async function updateSession(sessionId, sessionData) {
  sessionData.lastActivity = new Date().toISOString();
  
  // Try Supabase first, fallback to localStorage
  try {
    await updateSupabaseSession(sessionId, sessionData);
  } catch (supabaseError) {
    console.warn('Supabase unavailable, using localStorage:', supabaseError.message);
  }
  
  // Always update locally
  saveLocalSession(sessionId, sessionData);
  return sessionData;
}

export async function deleteSession(sessionId) {
  // Try Supabase first, fallback to localStorage
  try {
    await deleteSupabaseSession(sessionId);
  } catch (supabaseError) {
    console.warn('Supabase unavailable, using localStorage:', supabaseError.message);
  }
  
  // Always delete locally
  const sessions = getAllLocalSessions();
  delete sessions[sessionId];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  return true;
}

export async function updateSessionActivity(sessionId) {
  // Try Supabase first, fallback to localStorage
  try {
    await updateSupabaseSessionActivity(sessionId);
  } catch (supabaseError) {
    console.warn('Supabase unavailable, using localStorage:', supabaseError.message);
  }
  
  // Always update locally
  const sessions = getAllLocalSessions();
  const session = sessions[sessionId];
  
  if (session) {
    session.lastActivity = new Date().toISOString();
    saveLocalSession(sessionId, session);
    return session;
  }
  
  return null;
}